import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCachedTexts } from '../hooks/useCachedTexts';
import './ContactPage.css';

const ContactPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [sending, setSending] = useState(false);
  const { getText } = useCachedTexts('contact');

  useEffect(() => {
    document.title = 'Contact | Magicomms';
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    
    // Create mailto link with the form data
    const emailAddress = getText('email_address', 'pr@magicomms.com');
    const subject = encodeURIComponent('Contact from Website');
    const body = encodeURIComponent(`From: ${email}\n\n${message}`);
    window.location.href = `mailto:${emailAddress}?subject=${subject}&body=${body}`;
    
    setStatus(getText('form_success_message', 'Opening your email client...'));
    setSending(false);
    
    // Reset form after a delay
    setTimeout(() => {
      setEmail('');
      setMessage('');
      setStatus('');
    }, 3000);
  };

  return (
    <div className="contact-page">
      <div className="contact-container">
        <h1 className="contact-page-title">{getText('title', t('contact.title'))}</h1>
        
        <div className="contact-content">
          <div className="contact-info">
            <div className="contact-details">
              <a href="mailto:pr@magicomms.com" className="contact-item">
                <div className="contact-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <div className="contact-text">
                  <span className="contact-label">{getText('form_email_label', t('contact.emailLabel'))}</span>
                  <span className="contact-value">{getText('email_address', 'pr@magicomms.com')}</span>
                </div>
              </a>

              <div className="contact-item">
                <div className="contact-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                </div>
                <div className="contact-text">
                  <span className="contact-label">{getText('office_label', t('contact.officeLabel'))}</span>
                  <span className="contact-value">{getText('office_address', 'Via Properzio 5, 00193 Roma - Italy')}</span>
                </div>
              </div>

              <a 
                href="https://instagram.com/magicomms" 
                target="_blank" 
                rel="noopener noreferrer"
                className="contact-item"
              >
                <div className="contact-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <div className="contact-text">
                  <span className="contact-label">Instagram</span>
                  <span className="contact-value">{getText('instagram_handle', '@magicomms')}</span>
                </div>
              </a>

              <a 
                href="https://www.linkedin.com/company/magicomms" 
                target="_blank" 
                rel="noopener noreferrer"
                className="contact-item"
              >
                <div className="contact-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </div>
                <div className="contact-text">
                  <span className="contact-label">LinkedIn</span>
                  <span className="contact-value">{getText('linkedin_name', 'Magicomms')}</span>
                </div>
              </a>
            </div>
          </div>

          <div className="contact-form-wrapper">
            <form className="contact-form" onSubmit={handleSubmit}>
              <h2>{getText('form_submit_button', t('contact.send'))}</h2>
              
              <div className="form-group">
                <label htmlFor="email">{getText('form_email_label', t('contact.email'))}</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={getText('form_email_placeholder', t('contact.emailPlaceholder'))}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">{getText('form_message_label', t('contact.message'))}</label>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={getText('form_message_placeholder', t('contact.messagePlaceholder'))}
                  rows={5}
                  required
                />
              </div>

              <button type="submit" className="submit-btn" disabled={sending}>
                {sending ? getText('form_sending_button', 'Sending...') : getText('form_submit_button', t('contact.send'))}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"/>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
              </button>

              {status && <p className="form-status">{status}</p>}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
